import { Link as RouterLink, useLocation } from "react-router";
import { bool } from "prop-types";
import React from "react";
import { AppBar, Link, Toolbar, Typography, Box } from "@mui/material";
import { styled } from "@mui/material/styles";
import { styledHeaderLinkSx } from "shared/theme/styles";

import Search from "../Search";
import HelpIcon from "@mui/icons-material/ContactSupport";
import UserProfile from "../UserProfile";
import AnalyticsLogo from "shared/styles/icons/analytics-logo.svg";
import ToolTabs from "../ToolTabs";
import LoginButton from "shared/chrome/LoginButton";
import ResourcesMenu from "shared/chrome/menus/ResourcesMenu";
import UpdatesMenu from "shared/chrome/menus/UpdatesMenu";
import SkipToContentLink from "shared/chrome/SkipToContentLink";
import googleAnalytics from "shared/analytics";

const StyledAppBar = styled(AppBar)(({ theme }) => ({
  backgroundColor: theme.palette.common.white,
  display: "flex",
  flexDirection: "row",
  paddingLeft: 15,
  borderBottom: `${theme.palette.grey[200]} 1px solid`,
  "&.justified": {
    justifyContent: "space-between",
  },
}));

const StyledToolbar = styled(Toolbar)({
  padding: 0,
  paddingLeft: 15,
  paddingRight: 15,
  flex: "1 1 auto",
  maxWidth: "100%",
  alignItems: "stretch",
});

const LogoLink = styled(RouterLink)({
  display: "flex",
  flex: "0 1 100px",
  alignItems: "center",
  justifyContent: "center",
  paddingLeft: 5,
  paddingRight: 5,
  minWidth: 100,
});

/**
 * Right now, this launches the "Review Demo Modal", but eventually,
 *   this could be a menu like UserProfile, where one of the links launches the same modal.
 */
const Help = () => {
  return (
    <Link
      href="mailto:analytics@icer.org"
      target="_blank"
      sx={(theme) => ({
        ...styledHeaderLinkSx({ theme, anchorEl: false }),
        "& svg:nth-of-type(1)": {
          marginRight: "8px",
        },
      })}
      onClick={() => googleAnalytics.trackHeaderNav({ action: "item", label: "Contact Us" })}
    >
      <HelpIcon />
      <Typography component="span">Contact Us</Typography>
    </Link>
  );
};

const LandingButtons = () => {
  return (
    <>
      <LoginButton />
    </>
  );
};

const Header = ({ isSignedIn, isDeviseView }) => {
  const location = useLocation();

  const isToolSelectorPage = location.pathname.includes("tools");
  const isGlobalPage = Boolean(
    location.pathname.match(
      /^\/(tools|webinars|videos|coming-soon|documents|updates|soon|account|password)$/,
    ),
  );
  const isLandingPage = !isSignedIn && !isDeviseView;

  const userProfileIsEnabled = !isLandingPage && isSignedIn && !isDeviseView;
  const showToolButtons = userProfileIsEnabled && !isLandingPage && !isToolSelectorPage;

  const isJustified = isLandingPage || isToolSelectorPage;

  return (
    <StyledAppBar
      id="main-header"
      position="fixed"
      className={isJustified ? "justified" : ""}
      elevation={0}
    >
      <LogoLink
        to="/"
        onClick={() => googleAnalytics.trackHeaderNav({ action: "item", label: "Logo (home)" })}
      >
        <img style={{ maxHeight: 40.5 }} src={AnalyticsLogo} alt="ICER Analytics" />
      </LogoLink>
      <SkipToContentLink />
      <StyledToolbar>
        <ToolTabs
          classes
          showAlways={showToolButtons}
          showActiveTab={!isGlobalPage}
          location={location}
        />
        <Box sx={{ marginLeft: "auto", display: "flex", alignItems: "center" }}>
          {isLandingPage && <LandingButtons />}
          <Box sx={{ display: "none" }}>{userProfileIsEnabled && <Search />}</Box>
          {userProfileIsEnabled && <ResourcesMenu />}
          {userProfileIsEnabled && <UpdatesMenu />}
          {userProfileIsEnabled && <Help />}
          {userProfileIsEnabled && <UserProfile />}
        </Box>
      </StyledToolbar>
    </StyledAppBar>
  );
};

Header.propTypes = {
  isSignedIn: bool.isRequired,
  isDeviseView: bool.isRequired,
};

export default Header;
